import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet } from 'react-native';
import { collection, onSnapshot, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { functions as configuredFunctions } from '../config/firebase';
import { useTheme } from '../contexts/ThemeContext';
import { MasterTourGuestListPage } from '../components/calendar/MasterTourOpsViews';
import { getFirestoreDb } from '../utils/calendarShareService';
import { getTabEntryDraftText, getTabEntryPublishedText } from '../utils/calendarTabDraftService';
import { GUEST_CAP_TAB, serializeGuestCap } from '../utils/calendarGuestList';

export default function GuestListVenueShare({ ownerId, link }) {
  const { theme, isDarkMode } = useTheme();
  const [tabs, setTabs] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dateKey = String(link?.dateKey || '');
  const tourId = String(link?.tourId || '');

  useEffect(() => {
    if (!ownerId || !tourId || !dateKey) {
      setTabs({});
      return undefined;
    }
    const db = getFirestoreDb();
    const qy = query(
      collection(db, 'users', ownerId, 'calendarTabEntries'),
      where('tourId', '==', tourId),
      where('date', '==', dateKey)
    );
    return onSnapshot(
      qy,
      (snap) => {
        const next = {};
        snap.forEach((d) => {
          const row = d.data() || {};
          const tabKey = String(row.tabKey || '');
          if (!tabKey) return;
          next[tabKey] = getTabEntryPublishedText(row) || getTabEntryDraftText(row);
        });
        setTabs(next);
      },
      () => setTabs({})
    );
  }, [ownerId, tourId, dateKey]);

  const persistGen = useRef(0);
  const persistSpots = useMemo(() => async (spots) => {
    const token = String(link?.token || '').trim();
    if (!token) return;
    const gen = persistGen.current + 1;
    persistGen.current = gen;
    setSaving(true);
    setError('');
    try {
      const fn = httpsCallable(configuredFunctions || getFunctions(), 'setGuestListSpots');
      await fn({ token, spots });
      if (gen !== persistGen.current) return;
      setError('');
    } catch (err) {
      if (gen !== persistGen.current) return;
      console.warn('setGuestListSpots', err);
      setError(err?.message || 'Could not save spots. Check the link and try again.');
      throw err;
    } finally {
      if (gen === persistGen.current) setSaving(false);
    }
  }, [link?.token]);

  return (
    <View style={[styles.wrap, { backgroundColor: theme.background.primary }]}>
      <MasterTourGuestListPage
        guestText={tabs['GUEST LIST'] || ''}
        venueName={link?.tourName || 'Guest list'}
        dateKey={dateKey}
        showDateKeys={[]}
        isDarkMode={isDarkMode}
        accent={theme.brand?.accent}
        spotsRaw={tabs[GUEST_CAP_TAB] || serializeGuestCap(link?.guestSpots)}
        onPersistSpots={persistSpots}
        venueMode
        spotsSaving={saving}
        spotsError={error}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: { width: '100%' },
});
